import React, { Component } from 'react';
import v from 'vudu';
import { styles as s, format as f, classes as c, colors } from 'stylesheet';
import Text from 'components/Text';
import get from 'utils/get';
import { TweenMax, TimelineLite } from 'gsap';
import EasePack from 'gsap/EasePack';
import Waypoint from 'react-waypoint';
import CountUp from 'countup.js';
import aspectRatio from 'utils/aspectRatio';
import Triangle from 'components/svgs/Triangle';
import Stickyfill from 'stickyfilljs';
import SegmentWrapper from 'lib/segment';

const ease = EasePack.Expo.easeOut;

const PANEL_HEIGHT = 150;
const SUPPORT_BLUR = false;

function range(value, low1, high1, low2, high2) {
  return low2 + (high2 - low2) * (value - low1) / (high1 - low1);
}

const localClasses = v({
  gearSubset: {
    '@composes': [s.pt6, s.pb6],
    display: 'block',
    position: 'sticky',
    top: 0,
    height: `${PANEL_HEIGHT}vh`
  },
  subsetCopy: {
    '@composes': [s.center]
  },
  modalHeader: {
    '@composes': [s.p2],
    boxShadow: '0px 6px 20px 4px rgba(0, 0, 0, 0.14);'
  },
  modalBody: {
    '@composes': [s.p2],
  },
  gearImageWrapper: {
    willChange: 'transform',
    position: 'relative',
    //transform: 'translateY(-50%)',
    //top: '50%',
    height: '100%',
    width: '100%',
  },
  gearImage: {
    willChange: 'transform',
    maxHeight: '100%',
    maxWidth: '100%',
    top: '50%',
    pointerEvents: 'none',
    userSelect: 'none',
    '-mozUserSelect': 'none',
    '-khtmlUserSelect': 'none',
    '-webkitUserSelect': 'none',
    '-oUserSelect': 'none'
  },
  gearImageBacking: aspectRatio(1, 1)({
    willChange: 'transform',
    textAlign: 'center',
    borderRadius: '50%',
    perspective: '20px',
    backgroundImage: 'linear-gradient(131deg, rgba(127, 1, 93, 0.4) 0%, rgba(52, 26, 110, 0.4) 100%)'
  }),
  list: {
    listStyleType: 'none',
    margin: 0,
    paddingLeft: '1.2rem'
  },
  logoImageWrapper: {
    willChange: 'transform',
    backgroundSize: 'contain',
    width: '100%',
    height: '100%',
    position: 'absolute',
    left: 0,
    top: 0,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: '50% 50%'
  },
  listItem: {
    cursor: 'pointer',
    'p': {
      transition: '300ms color'
    },
    ':hover p': { color: colors.razzmatazz }
  },
  listItemActive: {
    'p': { color: colors.razzmatazz }
  },
  triangle: {
    willChange: 'transform',
    marginTop: '6px',
    position: 'absolute',
  },
  logoContainer: {
    willChange: 'transform',
    position: 'relative',
    width: '80px',
    height: '40px',
    margin: 'auto',
    'img': {
      top: '50%',
      maxWidth: '100%',
      maxHeight: '100%',
      position: 'absolute'
    }
  },
  shortNameWrapper: {
    willChange: 'transform',
    marginBottom: '-40px'
  }
});

const COUNTUP_OPTIONS = {
  useEasing: true,
  useGrouping: true,
  separator: ',',
  decimal: '.',
  prefix: '$'
};

const renderModalBody = item => {
  return (
    <div>
      <div className={localClasses.modalHeader}>
        <Text variant={'modalHeadline'} color={'black'}>{item.name}</Text>
        <Text variant={'p'} color={'black'}>Retail Price: {item.price}</Text>
      </div>
      <div className={localClasses.modalBody}>
        <Text variant={'p'} color={'black'}>Overview</Text>
        <Text variant={'p'} color={'black'}>{item.description}</Text>
      </div>
    </div>
  );
}

const renderSubsetListItems = (items, navigateToItem, currentItem, listRefs) => {
  return items.map((item, i) => {
    let classes = localClasses.listItem;
    if (item === currentItem) classes += ` ${localClasses.listItemActive}`;
    return (
      <li className={classes} key={i} onClick={() => navigateToItem(item)} ref={r => listRefs.push(r)}>
        <Text variant={'pBold'} classes={f(c.pb1)} color={'pinkSlate'}>{item.name}</Text>
      </li>
    );
  });
}

const renderImageTrack = (items, imageRefs) => {
  return items.map((item, i) => {
    return (
      <div key={i} className='content' ref={r => imageRefs.push(r)} style={{willChange: 'transform'}}>
        <div className={f(localClasses.gearImageWrapper)} style={{willChange: 'transform'}}>
          <img className={f(localClasses.gearImage, c.float)} src={item.image} alt={item.title} />
        </div>
      </div>
    );
  });
}

const renderLogos = (items, logoRefs) => {
  return items.map((item, i) => {
    return <div className={localClasses.logoImageWrapper} ref={r => logoRefs.push(r)} style={{ backgroundImage: `url(${item.logo})`}}></div>
  });
}

export default class GearSubset extends Component {
  constructor(props) {
    super(...arguments);
    this.state = { currentItem: get(props, 'set.items', [])[0] }

    this.triRef = null;
    this.imageRefs = [];
    this.listRefs = [];
    this.logoRefs = [];
    this.unplayed = true;
  }

  componentDidMount() {
    Stickyfill.add(this.ref);

    // Setup Initial States
    this.imageRefs.forEach((imageRef, i) => {
      TweenMax.to(imageRef, 0, {
        x: `${540* i}%`,
        z: (i === 0 ? '0px' : '-40px')
      });
      if (SUPPORT_BLUR) {
        TweenMax.to(imageRef.children[0], 0, {
          'webkitFilter': (i === 0 ? 'blur(0px)' : 'blur(6px)')
        });
      }
    });

    this.logoRefs.forEach((logoRef, i) => {
      TweenMax.to(logoRef, 0, {
        y: (i === 0 ? 0 : 40),
        opacity: (i === 0 ? 1 : 0)
      });
    });

    // Setup Entrance Animation
    this.entryTimeline = new TimelineLite({ paused: true });
    this.entryTimeline
      .fromTo(this.headlineRef, 0.8, { opacity: 0, y: 10 }, { opacity: 1, y: 0, ease }, 's')
      .fromTo(this.subheadlineRef, 0.8, { opacity: 0, y: 10 }, { opacity: 1, y: 0, ease }, 's+=0.4')
      .fromTo(this.imageRefs[0], 1.4, { opacity: 0, y: -20 }, { opacity: 1, y: 0, ease }, 's+=0.2')
      .fromTo(this.shortNameRef, 1.4, { opacity: 0, y: 0 }, { opacity: 1, y: 0, ease }, 's+=0.2')
      .fromTo(this.backingRef, 1.8, { opacity: 0, y: 40 }, { opacity: 1, y: 0, ease }, 's+=0.2')
      .fromTo(this.listRef, 0.8, { opacity: 0, y: 10 }, { opacity: 1, y: 0, ease }, 's+=0.6')
      .fromTo(this.descRef, 0.8, { opacity: 0, y: 10 }, { opacity: 1, y: 0, ease }, 's+=0.7')
  }

  playEntryAnimation = () => {
    if (this.unplayed) {
      this.descTextRef.innerText = this.state.currentItem.description;
      this.shortNameRef.innerText = this.state.currentItem.shortName;
      this.unplayed = false;
      this.entryTimeline.play();
      new CountUp(this.costRef, 0, this.state.currentItem.price, 0, 2, COUNTUP_OPTIONS).start();
    }
  }

  readMore = () => {
    this.props.setModal({ body: renderModalBody(get(this, 'state.currentItem')) });
  }

  scrollToItem = item => {

    let items       = get(this, 'props.set.items', []);
    let newIndex    = items.indexOf(item);
    let elTop       = this.wrapperRef.offsetTop
    let totalHeight = this.wrapperRef.offsetHeight;
    let chunk       = (totalHeight / items.length);

    TweenMax.to(window, 0.5, { scrollTo: (chunk * newIndex) + elTop, ease });
  }

  navigateToItem = item => {
    if (item === this.state.currentItem) return;

    let currentIndex = get(this, 'props.set.items', []).indexOf(this.state.currentItem);
    let newIndex = get(this, 'props.set.items', []).indexOf(item);

    this.setState({ currentItem: item });

    this.animateListTriangle(newIndex);
    this.animatePriceChange(item.price);
    this.animateImages(newIndex, currentIndex);
    this.animateDescriptionChange(item.description);
    this.animateShortNameChange(item.shortName);
  }

  animateListTriangle = newIndex => {
    let newY = (this.listRefs[newIndex].getBoundingClientRect().top - this.listRefs[0].parentElement.getBoundingClientRect().top) + 2;
    TweenMax.to(this.triRef, 0.8, { y: newY, ease });
  }

  animateDescriptionChange = newCopy => {
    TweenMax.to(this.descTextRef, 0.4, {
      opacity: 0,
      onComplete: () => {
        this.descTextRef.innerText = newCopy;
        TweenMax.to(this.descTextRef, 0.4, { opacity: 1 });
      }
    });
  }

  animateShortNameChange = newCopy => {
    TweenMax.to(this.shortNameRef, 0.4, {
      opacity: 0,
      onComplete: () => {
        this.shortNameRef.innerText = newCopy;
        TweenMax.to(this.shortNameRef, 0.4, { opacity: 1 });
      }
    });
  }


  animateImages = (newIndex, currentIndex) => {
    let currentItemRef = this.imageRefs[currentIndex];
    let nextItemRef = this.imageRefs[newIndex];
    let otherRefs = this.imageRefs.filter(ref => !!ref && (ref !== currentItemRef) && (ref !== nextItemRef));

    let direction = newIndex > currentIndex ? 1 : -1;
    let directionString = newIndex > currentIndex ? '-' : '+';

    let steps;
    if (currentIndex > newIndex) { steps = currentIndex - newIndex }
    else { steps = newIndex - currentIndex }

    let t = (new TimelineLite())
      .to(currentItemRef, 0.8, { z: '-40px', ease }, 's')
      .to(this.logoRefs[currentIndex], 0.8, { y: 40, opacity: 0, ease }, 's')
      .to(nextItemRef, 0.8, { x: `${direction * steps * 180}%`, z: '0px', ease }, 's')
      .to(this.logoRefs[newIndex], 0.8, { y: 0, opacity: 1, ease }, 's')
      .to(nextItemRef, 0.8, { x: '0%', ease }, 's+=0.4')

    if (SUPPORT_BLUR) {
      t.to(currentItemRef.children[0], 0.8, { 'webkitFilter': 'blur(6px)' }, 's')
       .to(nextItemRef.children[0], 0.8, { 'webkitFilter': 'blur(0px)' }, 's')
    }

    t.to(currentItemRef, 0.8, { x: `${(currentIndex - newIndex) * 540}%`, ease }, 's+=0.4')

    otherRefs.forEach(r => {
      let myIndex = this.imageRefs.indexOf(r);
      t.to(r, 0.8, { x: `${(myIndex - newIndex)*540}%`, ease }, 's+=0.4');
    });
  }

  animatePriceChange(price) {
    new CountUp(this.costRef, 0, price, 0, 0.8, COUNTUP_OPTIONS).start();
  }

  renderTriggers = (items) => {
    return (items || []).map(item => {
      return (
        <div style={{ height: `${PANEL_HEIGHT}vh`}}>
          <Waypoint onEnter={() => this.navigateToItem(item)} />
        </div>
      )
    })
  }

  openCurrentItem = () => {
    window.open(this.state.currentItem.url);
    SegmentWrapper.didClickLearnMore(this.state.currentItem.name);
  }

  render() {
    const { title, description, items } = this.props.set;
    const { currentItem } = this.state;

    return (
      <div style={{ height: `${(items.length + 2) * PANEL_HEIGHT}vh` }} ref={r => this.wrapperRef = r}>
        <div className={localClasses.gearSubset} ref={r => this.ref = r}>
          <div className={f(c.row, c.clearfix, c.pb3)}>
            <div className={localClasses.subsetCopy}>
              <Text variant={'heavyHeadline'} textRef={r => this.headlineRef = r}>{title}</Text>
              <Text variant={'p'} textRef={r => this.subheadlineRef = r}>{description}</Text>
            </div>
          </div>

          <div className={f(c.row, c.clearfix, localClasses.shortNameWrapper, c.center)}>
            <Text variant={'gearBacking'} color={'blackberry'} textRef={r => this.shortNameRef = r}></Text>
          </div>

          <div className={f(c.row, c.clearfix, c.flex, c.alignCenter)}>
            <div className={f(c.col4, c.left)} ref={r => this.listRef = r}>
              <div>
                <div ref={r => this.triRef= r}>
                  <Triangle className={localClasses.triangle} />
                </div>
                <ul className={localClasses.list}>{renderSubsetListItems(items, this.scrollToItem, currentItem, this.listRefs)}</ul>
              </div>
            </div>

            <div className={f(localClasses.gearImageBacking, c.col4, c.left)} ref={r => this.backingRef = r}>
              {renderImageTrack(items, this.imageRefs)}
            </div>

            <div className={f(c.col4, c.left, c.pl4)} ref={r => this.descRef = r}>
              <Text variant={'pBoldUpper'} color={'razzmatazz'}>Retail Price</Text>
              <Text variant={'largeness'} classes={f(c.pb1)} textRef={r => this.costRef = r}></Text>
              <Text variant={'pBold'} classes={f(c.pb1)} textRef={r => this.descTextRef = r} color={'pinkSlate'}></Text>
              <div onClick={this.openCurrentItem} style={{ cursor: 'pointer' }}>
                <Text variant={'pBold'} color={'byzantine'}>Learn More</Text>
              </div>
            </div>
          </div>

          <div className={f(c.clearfix, localClasses.logoContainer)}>
            {renderLogos(items, this.logoRefs)}
          </div>
          <Waypoint onEnter={this.playEntryAnimation} />
        </div>

        {this.renderTriggers(items)}
      </div>
    );
  }
};



// WEBPACK FOOTER //
// ./src/components/GearSubset.js